import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import BarSpecialsPanel from '@/components/BarSpecialsPanel';
import OwnerCommandCenter from '@/components/OwnerCommandCenter';
import ShiftHub from '@/components/ShiftHub';
import OwnerSinglesNightPanel from '@/components/OwnerSinglesNightPanel';
import OwnerMediaGallery from '@/components/OwnerMediaGallery';
import OwnerPromoRedemptions from '@/components/OwnerPromoRedemptions';
import OwnerBroadcastPanel from '@/components/OwnerBroadcastPanel';
import SocialFeed from '@/components/SocialFeed';
import {
  Store, LogOut, TrendingUp, Users, Plus, X, Crown,
  Check, Loader2, Megaphone, BarChart3, Clock, Star,
  FileText, DollarSign, ArrowLeft, Camera, Upload, Image, Heart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BarOwnerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [owner, setOwner] = useState(null);
  const [bars, setBars] = useState([]);
  const [myBar, setMyBar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [checkingOut, setCheckingOut] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(false);

  // Forms state
  const [newSpecial, setNewSpecial] = useState('');
  const [specials, setSpecials] = useState([]);
  const [promoTitle, setPromoTitle] = useState('');
  const [promoDescription, setPromoDescription] = useState('');
  const [barPhotos, setBarPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [checkedInPatrons, setCheckedInPatrons] = useState([]);
  const [patronsLoading, setPatronsLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('bar_owner_token');
    if (!token) {
      navigate('/bar-owner/auth');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchOwnerData();
  }, []);

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentType = searchParams.get('payment');
    const promoType = searchParams.get('promo');
    const tab = searchParams.get('tab');

    if (tab) {
      setActiveTab(tab);
    }

    if (sessionId && paymentType === 'success') {
      pollSubscriptionStatus(sessionId);
    } else if (sessionId && promoType === 'success') {
      pollPromoStatus(sessionId);
    } else if (paymentType === 'cancelled') {
      toast.error('Payment was cancelled.');
      navigate('/bar-owner/dashboard', { replace: true });
    } else if (promoType === 'cancelled') {
      toast.error('Promotion purchase cancelled.');
      navigate('/bar-owner/dashboard', { replace: true });
    }
  }, [searchParams]);

  const fetchOwnerData = async () => {
    try {
      const [ownerRes, barsRes] = await Promise.all([
        axios.get(`${API}/bar-owner/me`),
        axios.get(`${API}/bars`)
      ]);

      setOwner(ownerRes.data);
      setBars(barsRes.data);

      if (ownerRes.data.bar_id) {
        const bar = barsRes.data.find(b => b.id === ownerRes.data.bar_id);
        setMyBar(bar);
        setSpecials(bar?.drink_specials || []);

        // Fetch promotions
        try {
          const promosRes = await axios.get(`${API}/bar-owner/my-promotions`);
          setPromotions(promosRes.data || []);
        } catch (e) { /* ignore */ }

        // Fetch bar photos
        try {
          const photosRes = await axios.get(`${API}/bars/${ownerRes.data.bar_id}/photos`);
          setBarPhotos(photosRes.data || []);
        } catch (e) { /* ignore */ }

        // Auto-load checked-in patrons
        try {
          const patronsRes = await axios.get(`${API}/bar-owner/patrons-checkedin`);
          setCheckedInPatrons(patronsRes.data.patrons || []);
        } catch (e) { /* ignore */ }

        // Fetch analytics if premium
        if (ownerRes.data.subscription_tier === 'premium') {
          try {
            const analyticsRes = await axios.get(`${API}/bar-owner/analytics`);
            setAnalytics(analyticsRes.data);
          } catch (e) { /* ignore */ }
        }
      }
    } catch (e) {
      console.error(e);
      if (e.response?.status === 401) {
        localStorage.removeItem('bar_owner_token');
        navigate('/bar-owner/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const pollSubscriptionStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error('Payment status check timed out.');
      setPollingStatus(false);
      return;
    }
    setPollingStatus(true);
    try {
      const res = await axios.get(`${API}/bar-owner/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid' || res.data.status === 'completed') {
        toast.success('Subscription activated! Welcome aboard!');
        setPollingStatus(false);
        fetchOwnerData();
        navigate('/bar-owner/dashboard', { replace: true });
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Payment session expired.');
        setPollingStatus(false);
        navigate('/bar-owner/dashboard', { replace: true });
        return;
      }
      setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      if (attempts < 9) setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2000);
      else { toast.error('Error checking payment.'); setPollingStatus(false); }
    }
  };

  const pollPromoStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error('Payment status check timed out.');
      setPollingStatus(false);
      return;
    }
    setPollingStatus(true);
    try {
      const res = await axios.get(`${API}/bar-owner/promote/status/${sessionId}`);
      if (res.data.payment_status === 'paid' || res.data.status === 'completed') {
        toast.success('Promoted post is now live!');
        setPollingStatus(false);
        fetchOwnerData();
        navigate('/bar-owner/dashboard', { replace: true });
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Payment session expired.');
        setPollingStatus(false);
        navigate('/bar-owner/dashboard', { replace: true });
        return;
      }
      setTimeout(() => pollPromoStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      if (attempts < 9) setTimeout(() => pollPromoStatus(sessionId, attempts + 1), 2000);
      else { toast.error('Error checking payment.'); setPollingStatus(false); }
    }
  };

  const handleClaimBar = async (barId) => {
    try {
      await axios.post(`${API}/bar-owner/claim-bar`, null, { params: { bar_id: barId } });
      toast.success('Bar claimed successfully!');
      fetchOwnerData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to claim bar');
    }
  };

  const handleSubscriptionCheckout = async (packageId) => {
    setCheckingOut(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/bar-owner/checkout`, null, {
        params: { package_id: packageId, origin_url: originUrl }
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const handlePromotedPostCheckout = async () => {
    if (!promoTitle.trim() || !promoDescription.trim()) {
      toast.error('Please fill in a title and description');
      return;
    }
    setCheckingOut('promo');
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/bar-owner/promote/checkout`, null, {
        params: {
          title: promoTitle,
          description: promoDescription,
          origin_url: originUrl
        }
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const handleUpdateSpecials = async () => {
    try {
      await axios.put(`${API}/bar-owner/update-specials`, specials);
      toast.success('Drink specials updated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update specials');
    }
  };

  const handleAddSpecial = () => {
    if (newSpecial.trim()) {
      setSpecials([...specials, newSpecial.trim()]);
      setNewSpecial('');
    }
  };

  const logout = () => {
    localStorage.removeItem('bar_owner_token');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/bar-owner/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (pollingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
        <Card className="glass p-8 border border-indigo-500/50 text-center max-w-md">
          <Loader2 className="w-16 h-16 text-indigo-400 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-white mb-2">Processing Payment...</h2>
          <p className="text-slate-400">Hang tight while we confirm your payment.</p>
        </Card>
      </div>
    );
  }

  const isSubscribed = owner?.subscription_status === 'active';

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Store },
    ...(isSubscribed ? [{ id: 'specials', label: 'Specials', icon: Star }] : []),
    ...(myBar ? [{ id: 'photos', label: 'Photos', icon: Camera }] : []),
    ...(myBar ? [{ id: 'patrons', label: 'Patrons', icon: Users }] : []),
    { id: 'shifthub', label: 'Shift Hub', icon: Users },
    { id: 'singlesnight', label: 'Singles Night', icon: Heart },
    { id: 'media', label: 'Media Gallery', icon: Image },
    { id: 'promote', label: 'Promote', icon: Megaphone },
    ...(owner?.subscription_tier === 'premium' ? [{ id: 'analytics', label: 'Analytics', icon: BarChart3 }] : []),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900/50 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700/50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Store className="w-6 h-6 text-indigo-400" />
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Bar Owner Hub
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isSubscribed && (
              <Badge data-testid="subscription-badge" className="bg-green-600 text-white text-xs">
                <Crown className="w-3 h-3 mr-1" />
                {owner?.subscription_tier === 'premium' ? 'Premium' : 'Active'}
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {/* Tab Navigation */}
        <div className="container mx-auto px-4 flex gap-1 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 space-y-6 max-w-4xl">

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            {/* Command Center (v45.1 recording layout) */}
            <div className="mb-6">
              <OwnerCommandCenter
                barName={myBar?.name || owner?.business_name || 'YOUR BAR'}
                tagline={myBar?.city ? `${myBar.city} • Premium Nightlife Venue` : 'Premium Nightlife Venue'}
              />
            </div>

            {/* Welcome Card */}
            <Card data-testid="welcome-card" className="glass p-5 border border-slate-700/50">
              <h2 className="text-xl font-bold text-white mb-1">
                Welcome, {owner?.business_name}
              </h2>
              <p className="text-slate-400 text-sm">
                {myBar ? `Managing: ${myBar.name}` : 'Claim a bar to get started'}
              </p>
            </Card>

            {/* Promo Redemptions (First Timer / Group of 4 / Birthday) */}
            <OwnerPromoRedemptions />

            {/* Push Blast + Auto-Ads + Low-Density Threshold */}
            <OwnerBroadcastPanel barId={myBar?.id || owner?.bar_id} />

            {/* Claim Bar (if no bar claimed) */}
            {!myBar && (
              <Card data-testid="claim-bar-section" className="glass p-5 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">Claim Your Bar</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {bars.filter(b => !b.claimed).map(bar => (
                    <Card key={bar.id} className="glass p-3 border border-slate-600/50">
                      <h4 className="font-medium text-white text-sm">{bar.name}</h4>
                      <p className="text-xs text-slate-400 mb-2">{bar.address}</p>
                      <Button
                        data-testid={`claim-bar-${bar.id}`}
                        size="sm"
                        onClick={() => handleClaimBar(bar.id)}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-xs"
                      >
                        Claim This Bar
                      </Button>
                    </Card>
                  ))}
                </div>
              </Card>
            )}

            {/* Subscription Plans (if not subscribed) */}
            {myBar && !isSubscribed && (
              <div data-testid="subscription-plans">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Choose Your Plan
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Annual Plan - Best Deal */}
                  <Card className="glass p-5 border-2 border-amber-500/40 relative">
                    <Badge className="absolute -top-2.5 right-3 bg-amber-500 text-black text-xs">
                      BEST DEAL
                    </Badge>
                    <h4 className="text-xl font-bold text-white mb-1">Annual Plan</h4>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-3xl font-bold text-amber-400">$1,200</span>
                      <span className="text-slate-400 text-sm">/year</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-4">
                      Paid upfront. Save $600/yr vs month-to-month.
                    </p>
                    <ul className="space-y-1.5 mb-5">
                      {['Happy Hour updates', 'Singles Night listings', 'Analytics dashboard', 'Featured placement', 'Unlimited specials'].map(f => (
                        <li key={f} className="flex items-center gap-2 text-slate-300 text-sm">
                          <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      data-testid="checkout-annual-plan"
                      onClick={() => handleSubscriptionCheckout('annual_plan')}
                      disabled={checkingOut !== null}
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
                    >
                      {checkingOut === 'annual_plan' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                      ) : (
                        <><Crown className="w-4 h-4 mr-2" />Pay $1,200/yr</>
                      )}
                    </Button>
                  </Card>

                  {/* Annual Contract */}
                  <Card className="glass p-5 border border-indigo-500/40 relative">
                    <Badge className="absolute -top-2.5 right-3 bg-indigo-500 text-white text-xs">
                      POPULAR
                    </Badge>
                    <h4 className="text-xl font-bold text-white mb-1">Annual Contract</h4>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-3xl font-bold text-indigo-400">$125</span>
                      <span className="text-slate-400 text-sm">/month</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-4">
                      1-year commitment. First month due at signup.
                    </p>
                    <ul className="space-y-1.5 mb-5">
                      {['Happy Hour updates', 'Singles Night listings', 'Analytics dashboard', 'Featured placement', 'Unlimited specials'].map(f => (
                        <li key={f} className="flex items-center gap-2 text-slate-300 text-sm">
                          <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      data-testid="checkout-annual-contract"
                      onClick={() => handleSubscriptionCheckout('annual_contract')}
                      disabled={checkingOut !== null}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      {checkingOut === 'annual_contract' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                      ) : (
                        <><DollarSign className="w-4 h-4 mr-2" />Start - $125</>
                      )}
                    </Button>
                  </Card>

                  {/* Month-to-Month */}
                  <Card className="glass p-5 border border-slate-600/50">
                    <h4 className="text-xl font-bold text-white mb-1">Month-to-Month</h4>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-3xl font-bold text-slate-300">$200</span>
                      <span className="text-slate-400 text-sm">/month</span>
                    </div>
                    <p className="text-xs text-slate-400 mb-4">
                      No contract. First month due at signup.
                    </p>
                    <ul className="space-y-1.5 mb-5">
                      {['Happy Hour updates', 'Singles Night listings', 'Analytics dashboard', 'Featured placement', 'Cancel anytime'].map(f => (
                        <li key={f} className="flex items-center gap-2 text-slate-300 text-sm">
                          <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      data-testid="checkout-m2m"
                      onClick={() => handleSubscriptionCheckout('month_to_month')}
                      disabled={checkingOut !== null}
                      className="w-full bg-slate-700 hover:bg-slate-600"
                    >
                      {checkingOut === 'month_to_month' ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                      ) : (
                        <><DollarSign className="w-4 h-4 mr-2" />Start - $200</>
                      )}
                    </Button>
                  </Card>
                </div>
              </div>
            )}

            {/* Quick Stats (if subscribed) */}
            {isSubscribed && myBar && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="glass p-3 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-indigo-400">{analytics?.total_checkins || 0}</p>
                  <p className="text-xs text-slate-400">Total Check-ins</p>
                </Card>
                <Card className="glass p-3 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-green-400">{analytics?.checkins_today || 0}</p>
                  <p className="text-xs text-slate-400">Today</p>
                </Card>
                <Card className="glass p-3 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-purple-400">{analytics?.unique_visitors || 0}</p>
                  <p className="text-xs text-slate-400">Unique Visitors</p>
                </Card>
                <Card className="glass p-3 border border-slate-700/50 text-center">
                  <p className="text-2xl font-bold text-amber-400">{promotions.length}</p>
                  <p className="text-xs text-slate-400">Active Promos</p>
                </Card>
              </div>
            )}
          </>
        )}

        {/* SHIFT HUB TAB */}
        {activeTab === 'shifthub' && (
          <ShiftHub />
        )}

        {/* SINGLES NIGHT TAB */}
        {activeTab === 'singlesnight' && (
          <OwnerSinglesNightPanel />
        )}

        {/* MEDIA GALLERY TAB */}
        {activeTab === 'media' && (
          <>
            <OwnerMediaGallery />
            <SocialFeed
              feedKey="owner"
              tokenKey="bar_owner_token"
              title="📣 Venue Social Feed"
              accentColor="#00CCFF"
              glowColor="#0088CC"
              showLike={false}
            />
          </>
        )}

        {/* SPECIALS TAB */}
        {activeTab === 'specials' && isSubscribed && myBar && (
          <Card data-testid="specials-section" className="glass p-5 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" />
              Drink Specials
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  data-testid="new-special-input"
                  placeholder="e.g. $5 Margaritas till 8pm"
                  value={newSpecial}
                  onChange={(e) => setNewSpecial(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSpecial()}
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <Button data-testid="add-special-btn" onClick={handleAddSpecial} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {specials.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No specials yet. Add your first one above!</p>
              ) : (
                <div className="space-y-2">
                  {specials.map((special, index) => (
                    <div key={index} className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-lg">
                      <span className="text-slate-300 text-sm">{special}</span>
                      <button onClick={() => setSpecials(specials.filter((_, i) => i !== index))} className="text-red-400 hover:text-red-300">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Button data-testid="save-specials-btn" onClick={handleUpdateSpecials} className="w-full bg-indigo-600 hover:bg-indigo-700">
                Save Specials
              </Button>
            </div>

            {/* Live time-limited specials with push notifications */}
            <div className="mt-6">
              <BarSpecialsPanel />
            </div>
          </Card>
        )}


        {/* PHOTOS TAB */}
        {activeTab === 'photos' && myBar && (
          <Card data-testid="photos-section" className="glass p-5 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
              <Camera className="w-5 h-5 text-indigo-400" />
              Bar Photos
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Upload photos of your bar, food, drinks, and events. These show up on the NightBuZz feed.
            </p>

            {/* Upload button */}
            <label
              data-testid="photo-upload-label"
              className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer mb-4 transition-all ${
                uploadingPhoto
                  ? 'border-indigo-500/50 bg-indigo-500/5'
                  : 'border-slate-600 hover:border-indigo-500/50 hover:bg-indigo-500/5'
              }`}
            >
              {uploadingPhoto ? (
                <><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /><span className="text-slate-300 text-sm">Uploading...</span></>
              ) : (
                <><Upload className="w-5 h-5 text-indigo-400" /><span className="text-slate-300 text-sm">Tap to upload a photo</span></>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploadingPhoto(true);
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('caption', '');
                  try {
                    const token = localStorage.getItem('bar_owner_token');
                    await axios.post(`${API}/bars/${owner.bar_id}/photos`, formData, {
                      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                    });
                    toast.success('Photo uploaded!');
                    const photosRes = await axios.get(`${API}/bars/${owner.bar_id}/photos`);
                    setBarPhotos(photosRes.data || []);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Upload failed');
                  } finally {
                    setUploadingPhoto(false);
                    e.target.value = '';
                  }
                }}
              />
            </label>

            {/* Photo Grid */}
            {barPhotos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {barPhotos.map(photo => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden bg-slate-800 group">
                    <img
                      src={photo.url.startsWith('/api') ? `${API.replace('/api', '')}${photo.url}` : photo.url}
                      alt={photo.caption || 'Bar photo'}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = '/nightbuzz-logo.png'; e.target.className = 'w-full h-full object-contain p-4 opacity-30'; }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <span className="text-[10px] text-slate-300">
                        {photo.source === 'owner' ? 'Your upload' : 'Yelp'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Image className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No photos yet. Upload your first one!</p>
                <p className="text-xs mt-1">Yelp photos will show as default until you upload your own.</p>
              </div>
            )}
          </Card>
        )}

        {/* PATRONS TAB */}
        {activeTab === 'patrons' && myBar && (
          <Card className="glass p-5 border border-slate-700/50" data-testid="patrons-checkedin-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-400" />
                Patrons Checked In
              </h3>
              <Button
                size="sm"
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={async () => {
                  setPatronsLoading(true);
                  try {
                    const res = await axios.get(`${API}/bar-owner/patrons-checkedin`);
                    setCheckedInPatrons(res.data.patrons || []);
                  } catch (err) {
                    toast.error('Failed to load patrons');
                  }
                  setPatronsLoading(false);
                }}
                data-testid="refresh-patrons-btn"
              >
                {patronsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            {checkedInPatrons.length > 0 ? (
              <div className="space-y-2">
                {checkedInPatrons.map((patron, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {patron.profile_picture_url ? (
                        <img src={patron.profile_picture_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        patron.username?.charAt(0)?.toUpperCase() || '?'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{patron.username}</p>
                      <p className="text-slate-400 text-xs">
                        {patron.gender && <span className="capitalize">{patron.gender}</span>}
                        {patron.age && <span> · Age {patron.age}</span>}
                      </p>
                    </div>
                    <div className="text-xs text-slate-500">
                      {patron.checked_in_at && new Date(patron.checked_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No patrons currently checked in</p>
                <p className="text-xs mt-1">Click Refresh to load the latest check-ins</p>
              </div>
            )}
          </Card>
        )}


        {/* PROMOTE TAB */}
        {activeTab === 'promote' && (
          <>
            {/* Buy a Promoted Post */}
            <Card data-testid="promote-section" className="glass p-5 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-pink-400" />
                Buy a Promoted Post
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                $10 per post. Your promotion will be featured on the NightBuZz homepage for 7 days.
              </p>

              {!myBar ? (
                <p className="text-amber-400 text-sm">Claim a bar first to create promotions.</p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Post Title</Label>
                    <Input
                      data-testid="promo-title-input"
                      placeholder="e.g. Half-Price Wings This Friday!"
                      value={promoTitle}
                      onChange={(e) => setPromoTitle(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Description</Label>
                    <Textarea
                      data-testid="promo-desc-input"
                      placeholder="Tell people what's happening..."
                      value={promoDescription}
                      onChange={(e) => setPromoDescription(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white"
                      rows={3}
                    />
                  </div>
                  <Button
                    data-testid="buy-promo-btn"
                    onClick={handlePromotedPostCheckout}
                    disabled={checkingOut === 'promo' || !promoTitle.trim() || !promoDescription.trim()}
                    className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                  >
                    {checkingOut === 'promo' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      <><DollarSign className="w-4 h-4 mr-2" />Pay $10 & Publish</>
                    )}
                  </Button>
                </div>
              )}
            </Card>

            {/* My Promotions */}
            {promotions.length > 0 && (
              <Card className="glass p-5 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-400" />
                  Your Promotions
                </h3>
                <div className="space-y-2">
                  {promotions.map(promo => {
                    const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
                    return (
                      <div key={promo.id} className={`p-3 rounded-lg border ${isExpired ? 'bg-slate-800/30 border-slate-700/30' : 'bg-slate-800/50 border-indigo-500/30'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-white text-sm">{promo.title}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{promo.description}</p>
                          </div>
                          <Badge className={isExpired ? 'bg-slate-600 text-slate-300' : 'bg-green-600 text-white'}>
                            {isExpired ? 'Expired' : 'Active'}
                          </Badge>
                        </div>
                        {promo.expires_at && (
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {isExpired ? 'Expired' : 'Expires'} {new Date(promo.expires_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && owner?.subscription_tier === 'premium' && analytics && (
          <div data-testid="analytics-section" className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-400" />
              Analytics Dashboard
            </h3>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="glass p-3 border border-slate-700/50">
                <p className="text-2xl font-bold text-white">{analytics.total_checkins}</p>
                <p className="text-xs text-slate-400">Total Check-ins</p>
              </Card>
              <Card className="glass p-3 border border-slate-700/50">
                <p className="text-2xl font-bold text-green-400">{analytics.checkins_last_30_days}</p>
                <p className="text-xs text-slate-400">Last 30 Days</p>
              </Card>
              <Card className="glass p-3 border border-slate-700/50">
                <p className="text-2xl font-bold text-purple-400">{analytics.unique_visitors || 0}</p>
                <p className="text-xs text-slate-400">Unique Visitors</p>
              </Card>
              <Card className="glass p-3 border border-slate-700/50">
                <p className="text-2xl font-bold text-amber-400">{analytics.retention_rate || 0}%</p>
                <p className="text-xs text-slate-400">Retention Rate</p>
              </Card>
            </div>

            {/* Today & Week */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="glass p-3 border border-green-500/20">
                <p className="text-xs text-green-400">Today</p>
                <p className="text-2xl font-bold text-white">{analytics.checkins_today || 0}</p>
              </Card>
              <Card className="glass p-3 border border-blue-500/20">
                <p className="text-xs text-blue-400">This Week</p>
                <p className="text-2xl font-bold text-white">{analytics.checkins_this_week || 0}</p>
              </Card>
              <Card className="glass p-3 border border-purple-500/20">
                <p className="text-xs text-purple-400">Loyal (5+ visits)</p>
                <p className="text-2xl font-bold text-white">{analytics.loyal_customers || 0}</p>
              </Card>
            </div>

            {/* Peak Times */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="glass p-3 border border-orange-500/20">
                <p className="text-xs text-orange-400 mb-0.5">Peak Hour</p>
                <p className="text-xl font-bold text-white">{analytics.peak_hour}</p>
              </Card>
              <Card className="glass p-3 border border-orange-500/20">
                <p className="text-xs text-orange-400 mb-0.5">Busiest Day</p>
                <p className="text-xl font-bold text-white">{analytics.peak_day}</p>
              </Card>
            </div>

            {/* Demographics */}
            {analytics.age_demographics && (
              <Card className="glass p-4 border border-slate-700/50">
                <h4 className="text-sm font-semibold text-white mb-2">Age Demographics</h4>
                <div className="grid grid-cols-5 gap-2">
                  {Object.entries(analytics.age_demographics).map(([range, count]) => (
                    <div key={range} className="text-center">
                      <p className="text-lg font-bold text-indigo-400">{count}</p>
                      <p className="text-xs text-slate-400">{range}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Export */}
            <div className="flex gap-3">
              <Button
                data-testid="view-patrons-btn"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/bar-owner/patrons`);
                    toast.success(`${res.data.total_patrons} patrons found`);
                  } catch (e) { toast.error('Failed to load patrons'); }
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Users className="w-4 h-4 mr-2" /> View Patrons
              </Button>
              <Button
                data-testid="export-csv-btn"
                onClick={() => window.open(`${API}/bar-owner/patrons/export`, '_blank')}
                variant="outline"
                className="border-indigo-500 text-indigo-400 hover:bg-indigo-500/10"
              >
                Export CSV
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarOwnerDashboard;
